import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Briefcase, FileText, Camera, Clock, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays } from "date-fns";

interface UsageData {
  totalJobs: number;
  jobsThisMonth: number;
  jobsLast7Days: number;
  totalMeasurements: number;
  totalEstimates: number;
  totalProposals: number;
  orgUsage: {
    id: string;
    name: string;
    jobCount: number;
    lastActivity: string | null;
  }[];
}

export function UsageMetrics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<UsageData | null>(null);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const sevenDaysAgo = subDays(now, 7);

      // Get total jobs
      const { count: totalJobs } = await supabase
        .from("jobs")
        .select("*", { count: "exact", head: true });

      // Get jobs this month
      const { count: jobsThisMonth } = await supabase
        .from("jobs")
        .select("*", { count: "exact", head: true })
        .gte("created_at", startOfMonth.toISOString());

      // Get jobs last 7 days
      const { count: jobsLast7Days } = await supabase
        .from("jobs")
        .select("*", { count: "exact", head: true })
        .gte("created_at", sevenDaysAgo.toISOString());

      // Get total measurements
      const { count: totalMeasurements } = await supabase
        .from("measurements_roof")
        .select("*", { count: "exact", head: true });

      // Get total estimates
      const { count: totalEstimates } = await supabase
        .from("job_estimates")
        .select("*", { count: "exact", head: true });

      // Get total proposals - using contingency_forms as proxy since proposals table may not exist
      const { count: totalProposals } = await supabase
        .from("contingency_forms")
        .select("*", { count: "exact", head: true })
        .eq("form_type", "proposal");

      // Get org usage
      const { data: orgs } = await supabase
        .from("organizations")
        .select("id, name");

      const orgUsage: UsageData["orgUsage"] = [];

      if (orgs) {
        for (const org of orgs.slice(0, 10)) { // Top 10
          const { count: jobCount } = await supabase
            .from("jobs")
            .select("*", { count: "exact", head: true })
            .eq("org_id", org.id);

          const { data: lastJob } = await supabase
            .from("jobs")
            .select("created_at")
            .eq("org_id", org.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          orgUsage.push({
            id: org.id,
            name: org.name,
            jobCount: jobCount || 0,
            lastActivity: lastJob?.created_at || null,
          });
        }
      }

      // Sort by job count descending
      orgUsage.sort((a, b) => b.jobCount - a.jobCount);

      setData({
        totalJobs: totalJobs || 0,
        jobsThisMonth: jobsThisMonth || 0,
        jobsLast7Days: jobsLast7Days || 0,
        totalMeasurements: totalMeasurements || 0,
        totalEstimates: totalEstimates || 0,
        totalProposals: totalProposals || 0,
        orgUsage,
      });
    } catch (error) {
      console.error("Error loading usage data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load usage data
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage Summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Total Jobs
            </CardDescription>
            <CardTitle className="text-3xl">{data.totalJobs}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 text-sm">
              <span className="text-muted-foreground">This month: <strong>{data.jobsThisMonth}</strong></span>
              <span className="text-muted-foreground">Last 7d: <strong>{data.jobsLast7Days}</strong></span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Measurements
            </CardDescription>
            <CardTitle className="text-3xl">{data.totalMeasurements}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">AI roof measurements generated</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Proposals & Estimates
            </CardDescription>
            <CardTitle className="text-3xl">{data.totalEstimates + data.totalProposals}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 text-sm">
              <span className="text-muted-foreground">Estimates: <strong>{data.totalEstimates}</strong></span>
              <span className="text-muted-foreground">Proposals: <strong>{data.totalProposals}</strong></span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Organization Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Usage by Organization
          </CardTitle>
          <CardDescription>Top organizations by job count</CardDescription>
        </CardHeader>
        <CardContent>
          {data.orgUsage.length > 0 ? (
            <div className="space-y-3">
              {data.orgUsage.map((org, index) => (
                <div key={org.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground w-6">{index + 1}.</span>
                    <div>
                      <p className="font-medium">{org.name}</p>
                      {org.lastActivity && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last activity: {format(new Date(org.lastActivity), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary">{org.jobCount} jobs</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No organization data available
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
